import React, { useState, useMemo, useEffect, useContext, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import styled from 'styled-components'
import { FiClock, FiTrendingUp } from "react-icons/fi";
import { Button } from "react-bootstrap";
import axios from "axios";
import {
  Meta,
  Item,
  Header,
  Left,
  PairBadge,
  Right,
  Chevron,
  Panel,
  Rate,
} from "./TradeViewIntergrationContainer.styles";

const QUOTE_REFRESH_INTERVAL_MS = 5000;
import BopCodeComponent from "./components/BopCodeComponent";
import AuthContext from "../../../../../auth/auth-context";
import TradeContext from "../../../../../store/tradeState/tradeContext";

const DELIVERY_TYPES = [
  {
    label: "Next Day",
    value: "Tom",
  },
  {
    label: "Spot",
    value: "Spot",
  },
  {
    label: "Value Today",
    value: "Cash",
  },
];


const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
`

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 480px;
  width: 90%;
  padding: 24px;
`

const ModalTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #212529;
`

const ModalText = styled.p`
  margin: 0 0 24px;
  color: #6c757d;
  line-height: 1.6;
`

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`


function CapitecTradeComponent(props) {
  const { inTrade } = props;

  const [deliveryType, setDeliveryType] = useState("");
  const [quote, setQuote] = useState(null);
  const [autoQuoteEnabled, setAutoQuoteEnabled] = useState(false);
  const [quoteError, setQuoteError] = useState(false);
  const [quoteErrorMessage, setQuoteErrorMessage] = useState("");

  const [trade, setTrade] = useState(null);
  const [tradeError, setTradeError] = useState(false);
  const [tradeErrorMessage, setTradeErrorMessage] = useState("");
  const [tradeOpen, setTradeOpen] = useState(true);
  const [tradeSuccess, setTradeSuccess] = useState(false);
  const [tradeSuccessMessage, setTradeSuccessMessage] = useState("");
  const [tradeStatus, setTradeStatus] = useState(null);
  const [checkStatusLoading, setCheckStatusLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const [bopScreenOpen, setBopScreenOpen] = useState(false);
  const [selectedBopCode, setSelectedBopCode] = useState(null);
  const [bopCodes, setBopCodes] = useState([]);

  const [loading, setLoading] = useState(false);
  const [tradeLoading, setTradeLoading] = useState(false);
  const [rateConfirmation, setRateConfirmation] = useState({});
  const deliveryTypeRef = useRef(deliveryType);
  const quoteRequestInFlightRef = useRef(false);
  const quoteAbortControllerRef = useRef(null);

  useEffect(() => {
    deliveryTypeRef.current = deliveryType;
  }, [deliveryType]);

  const authCtx = useContext(AuthContext);
  const tradeContext = useContext(TradeContext);

  const [submittedTradeDeliveryType, setSubmittedTradeDeliveryType] =
    useState("");
  const [submittedTradeObject, setSubmittedTradeObject] = useState(null);

  const headers = useMemo(
    () => ({
      "Acting-as": "dealer",
      Authorization: `JWT ${authCtx?.token}`,
    }),
    [authCtx?.token],
  );

  const tradeAllowed = useMemo(() => {
    const status = props?.inTrade?.status;

    if (
      status === "CANCELLED" ||
      status === "COMPLETED" ||
      status === "BOOKED"
    ) {
      return false;
    }
    return true;
  }, [props?.inTrade?.status]);

  const formatQuote = useCallback(
    (quoteData, deliveryTypeOverride) => {
      if (!quoteData) return null;

      const resolvedDeliveryType =
        quoteData.delivery_type ?? deliveryTypeOverride ?? deliveryTypeRef.current;

      const currencyPair =
        quoteData.currency_pair ??
        (inTrade?.trade_currency?.alpha_3 && inTrade?.fx_currency?.alpha_3
          ? `${inTrade.trade_currency.alpha_3}/${inTrade.fx_currency.alpha_3}`
          : undefined);
      const amount =
        quoteData.amount ?? inTrade?.fx_amount ?? inTrade?.trade_amount;
      const clientRate = quoteData.client_rate ?? quoteData.quote_rate;
      const dealRate = quoteData.client_rate_with_margin ?? quoteData.deal_rate;

      return {
        id: quoteData.id,
        local_trade_id:
          quoteData.associated_local_trade_id ?? quoteData.associated_local_trade,
        transaction_id: quoteData.transaction_id,
        quote_id: quoteData.capitec_quote_id ?? quoteData.associated_quote_id,
        createdAt: quoteData.request_date_time,
        deal_date_time: quoteData.deal_date_time,
        currencyPair,
        client_rate: clientRate,
        max_client_rate: quoteData.max_client_rate ?? inTrade?.max_exchange_rate,
        max_quote_client_rate: quoteData.max_client_rate,
        client_rate_with_margin: dealRate,
        delivery_type: resolvedDeliveryType,
        rate: dealRate ?? clientRate,
        sell_currency: quoteData.sell_currency,
        settlement_date: quoteData.settlement_date,
        amount,
      };
    },
    [inTrade],
  );

  const formatAmountValue = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "—";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatRateValue = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "—";
    return num.toFixed(4);
  };

  const formatTrade = useCallback(
    (tradeData) => {
      if (!tradeData) return {};
      return {
        id: tradeData.id,
        associated_local_trade_id: tradeData.associated_local_trade_id,
        associated_quote_id: tradeData.associated_quote_id,
        deal_rate: tradeData.deal_rate,
        deal_transaction_id: tradeData.deal_transaction_id,
        quote_rate: tradeData.quote_rate,
        transaction_id: tradeData.transaction_id,
        request_date_time: tradeData.request_date_time,
        account_number: inTrade.account_number,
      };
    },
    [inTrade],
  );

  const hasTrade = Boolean(trade && Object.keys(trade).length > 0);

  const abortInFlightQuoteRequest = useCallback(() => {
    quoteRequestInFlightRef.current = false;

    if (quoteAbortControllerRef.current) {
      quoteAbortControllerRef.current.abort();
      quoteAbortControllerRef.current = null;
    }
  }, []);

  const generateCapitecQuote = useCallback(
    async ({ silent = false } = {}) => {
      const activeDeliveryType = deliveryTypeRef.current;

      if (quoteRequestInFlightRef.current) {
        return false;
      }

      if (!silent) {
        setQuoteError(false);
        setQuoteErrorMessage("");
        setTradeError(false);
        setTradeErrorMessage("");
      }

      if (!activeDeliveryType) {
        if (!silent) {
          setQuoteError(true);
          setQuoteErrorMessage("Please select a delivery type.");
        }
        return false;
      }

      if (!silent) {
        setLoading(true);
      }

      quoteRequestInFlightRef.current = true;

      const abortController = new AbortController();
      quoteAbortControllerRef.current = abortController;

      try {
        if (props?.inTrade?.status === "CANCELLED") {
          if (!silent) {
            setQuoteError(true);
            setQuoteErrorMessage("Cannot generate quote for a cancelled trade.");
          }
          return false;
        }

        const body = {
          associated_local_trade_id: props?.inTrade?.id,
          delivery_type: activeDeliveryType,
        };
        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/capitec/fx/quote/create/`,
          body,
          { headers, signal: abortController.signal },
        );

        const formattedQuote = formatQuote(
          { ...response.data, delivery_type: activeDeliveryType },
          activeDeliveryType,
        );
        setQuote(formattedQuote);
        setAutoQuoteEnabled(true);
        if (!silent) {
          setQuoteError(false);
          setQuoteErrorMessage("");
        }
        return true;
      } catch (error) {
        if (axios.isCancel(error)) {
          return false;
        }
        console.log("Error generating quote", error);
        if (!silent) {
          setQuoteError(true);
          setQuoteErrorMessage(
            error.response?.data?.error ||
              "An error occurred while generating the quote.",
          );
        }
        return false;
      } finally {
        if (quoteAbortControllerRef.current === abortController) {
          quoteAbortControllerRef.current = null;
        }
        quoteRequestInFlightRef.current = false;
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [formatQuote, headers, props?.inTrade?.id, props?.inTrade?.status],
  );

  useEffect(() => {
    let cancelled = false;

    async function initializeCapitecState() {
      if (!props.inTrade?.id) return;

      let activeTrades = [];

      try {
        const trades = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/capitec/fx/quote/book-responses-by-local-trade/`,
          {
            headers,
            params: { local_trade_id: props.inTrade.id },
          },
        );
        activeTrades = (trades.data || [])
          .filter((t) => t.status === "ACTIVE")
          .sort(
            (a, b) =>
              new Date(b.request_date_time) - new Date(a.request_date_time),
          );
        if (!cancelled && activeTrades.length > 0) {
          setTrade(formatTrade(activeTrades[0]));
        }
      } catch (error) {
        console.log("Error fetching submitted trades", error);
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/capitec/fx/quote/by-trade-id?associated_trade_id=${props.inTrade.id}`,
          { headers },
        );
        if (cancelled) return;

        const formattedQuotes = (response.data || [])
          .map((quoteData) => formatQuote(quoteData))
          .filter(Boolean)
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );
        const latestQuote = formattedQuotes[0] ?? null;
        setQuote(latestQuote);

        if (!latestQuote) return;

        if (latestQuote.delivery_type) {
          deliveryTypeRef.current = latestQuote.delivery_type;
          setDeliveryType(latestQuote.delivery_type);
        }

        // Existing unexecuted quote: refresh immediately, then every 5s via auto-quote interval
        if (activeTrades.length === 0) {
          setAutoQuoteEnabled(true);
          await generateCapitecQuote({ silent: true });
        }
      } catch (error) {
        console.log("Error fetching Capitec quotes", error);
        if (!cancelled) {
          setQuote(null);
        }
      }
    }

    initializeCapitecState();

    return () => {
      cancelled = true;
    };
  }, [
    props.inTrade,
    formatQuote,
    formatTrade,
    headers,
    generateCapitecQuote,
  ]);

  useEffect(() => {
    if (!autoQuoteEnabled || hasTrade || !deliveryType) return undefined;

    const intervalId = setInterval(() => {
      generateCapitecQuote({ silent: true });
    }, QUOTE_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [autoQuoteEnabled, hasTrade, deliveryType, generateCapitecQuote]);

  useEffect(() => {
    if (!trade) return;

    fetchTradeStatus(trade);

    const intervalId = setInterval(async () => {
      const result = await fetchTradeStatus(trade);

      if (result && result.payload.dealStatus === "DEAL_RELEASED") {
        clearInterval(intervalId);
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [trade]);

  useEffect(() => {
    if (!trade || !quote) return;

    if (quote.id === trade.associated_quote_id) {
      setSubmittedTradeDeliveryType(quote.delivery_type);
    }
  }, [trade, quote]);

  const submitCapitecTrade = async (quoteId) => {
    abortInFlightQuoteRequest();

    try {
      setTradeError(false);
      setTradeErrorMessage("");
      setTradeSuccess(false);
      setTradeSuccessMessage("");
      setTradeLoading(true);
      setSubmittedTradeObject(null);
      setTradeStatus(null);
      const body = {
        capitec_quote_response_id: quoteId,
      };
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/capitec/fx/quote/book/`,
        body,
        { headers },
      );

      setTradeLoading(false);
      if (response.data.id) {
        setTrade(formatTrade(response.data));
        setAutoQuoteEnabled(false);
      }
    } catch (error) {
      console.log("Error submitting trade", error);
      setTradeLoading(false);
      setTradeError(true);
      setTradeErrorMessage(
        error.response?.data?.error ||
          "An error occurred while submitting the trade.",
      );
      setAutoQuoteEnabled(true);
    }
  };

  const fetchTradeStatus = async (tradeToCheck) => {
    try {
      setCheckStatusLoading(true);
      const body = {
        capitec_book_trade_response_id: tradeToCheck.id,
      };
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/capitec/fx/transaction-status/`,
        body,
        { headers: headers },
      );

      const { payload } = response.data;
      const { customer, fromSettlementAccount, thirdParty, trade } =
        response.data.payload.deal;
      // setTradeStatus(response.data?.status || response.data)
      setTradeStatus(payload?.dealStatus);
      setSubmittedTradeObject({
        ...submittedTradeObject,
        customer_no: customer?.cifNo,
        account_number: fromSettlementAccount?.accountNumber,
        currency_pair: trade?.currencyPair,
        amount: trade?.dealCurrencyAmount,
        trade_date: trade?.dealDate,
        settlement_date: trade?.settlementDate,
      });
      setCheckStatusLoading(false);
      return response.data;
    } catch (error) {
      console.log("Error fetching trade status", error);
      setCheckStatusLoading(false);
      return null;
    }
  };

  const cancelCapitecTrade = () => {
    setShowCancelModal(true);
  }

  const handleCancelTradeConfirm = async () => {
    try {
      setLoading(true);
      const body = {
        book_trade_response_id: trade.id,
        status: "CANCELLED",
      };
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/capitec/fx/update-book-trade-status/`,
        body,
        { headers: headers },
      );
      setTrade(null);
      setAutoQuoteEnabled(Boolean(quote));
      setLoading(false);
      setShowCancelModal(false);
      // Optionally update UI based on cancellation response
    } catch (error) {
      console.log("Error cancelling trade", error);
      setLoading(false);
      setTradeError(true);
      setTradeErrorMessage(
        error.response?.data?.error ||
          "An error occurred while cancelling the trade.",
      );
    }
  };

  const formatDate = useMemo(() => (iso) => new Date(iso).toLocaleString(), []);

  function isExpirationWithinTenMinutes(createdAt) {
    const ts = new Date(createdAt).getTime()
    if (!Number.isFinite(ts)) return false
    const now = Date.now()
    const delta = now - ts
    return delta > (60 * 60 * 1000)
  }

  return (
    <div>
      {!hasTrade && quote && (
        <>
          <Item>
            <Header
              as="div"
              aria-controls="quote-panel"
              $open
            >
              <Left>
                <PairBadge>{quote.currencyPair}</PairBadge>
                <Meta
                  title={quote.createdAt}
                  style={{
                    color: isExpirationWithinTenMinutes(quote.createdAt)
                      ? "#dc3545"
                      : undefined,
                  }}
                >
                  <FiClock style={{ marginRight: 6 }} />{" "}
                  {formatDate(quote.createdAt)}
                </Meta>
                {quote.delivery_type && (
                  <span
                    style={{
                      marginLeft: 8,
                      padding: "2px 6px",
                      backgroundColor: "#f1f3f5",
                      borderRadius: 4,
                      fontSize: "0.75rem",
                    }}
                  >
                    {quote.delivery_type}
                  </span>
                )}
              </Left>
              <Right>
                <Meta>Amount</Meta>
                <strong>{formatAmountValue(quote.amount)}</strong>
                <Meta>
                  <FiTrendingUp style={{ marginRight: 6 }} /> Created Rate
                </Meta>
                <Rate>{formatRateValue(quote.client_rate)}</Rate>
              </Right>
            </Header>

            <Panel
              id="quote-panel"
              role="region"
              aria-label="Quote details"
              $open
            >
              <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
                <li
                  style={{
                    padding: "8px 0",
                    borderBottom: "1px solid #f1f3f5",
                  }}
                >
                  <Meta>Created</Meta>:{" "}
                  <strong>{formatDate(quote.createdAt)}</strong>
                </li>
                <li
                  style={{
                    padding: "8px 0",
                    borderBottom: "1px solid #f1f3f5",
                  }}
                >
                  <Meta>Pair</Meta>: <strong>{quote.currencyPair}</strong>
                </li>
                <li
                  style={{
                    padding: "8px 0",
                    borderBottom: "1px solid #f1f3f5",
                  }}
                >
                  <Meta>Amount</Meta>:{" "}
                  <strong>{formatAmountValue(quote.amount)}</strong>
                </li>
                <li style={{ padding: "8px 0" }}>
                  <Meta>Max Client Rate</Meta>:{" "}
                  <strong>{formatRateValue(quote.max_client_rate)}</strong>
                </li>
                <li style={{ padding: "8px 0" }}>
                  <Meta>Final Trade Rate</Meta>:{" "}
                  <strong>{formatRateValue(quote.rate)}</strong>
                </li>
              </ul>
              {Number(quote.rate) > Number(props.inTrade.trade_rate) && (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginTop: 12,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!rateConfirmation[quote.id]}
                    onChange={(e) =>
                      setRateConfirmation((prev) => ({
                        ...prev,
                        [quote.id]: e.target.checked,
                      }))
                    }
                    style={{ cursor: "pointer" }}
                  />
                  <span style={{ fontSize: "0.9rem", color: "#dc3545" }}>
                    I confirm that the final trade rate exceeds the maximum
                    approved trade rate of (
                    {formatRateValue(quote.max_client_rate)}), and the client
                    has explicitly given their approval for us to proceed with
                    the execution of the trade at this rate.
                  </span>
                </label>
              )}
              {!isExpirationWithinTenMinutes(quote.createdAt) && (
                <Button
                  disabled={
                    tradeLoading ||
                    (Number(quote.rate) > Number(props.inTrade.trade_rate) &&
                      !rateConfirmation[quote.id])
                  }
                  variant="outline-primary"
                  style={{ marginTop: 16 }}
                  onClick={() => submitCapitecTrade(quote.id)}
                >
                  {tradeLoading ? "Processing…" : "Submit Trade"}
                </Button>
              )}
            </Panel>
          </Item>

          {tradeError && (
            <div style={{ marginTop: 12, color: "red" }}>
              {tradeErrorMessage ||
                "An error occurred while submitting the trade."}
            </div>
          )}
          {tradeSuccess && (
            <div style={{ marginTop: 12, color: "green" }}>
              {tradeSuccessMessage || "Trade submitted successfully."}
            </div>
          )}

          {tradeAllowed === true && (
            <div style={{ marginTop: 16 }}>
              <Button
                variant="warning"
                style={{ width: "250px", minWidth: "250px", maxWidth: "250px", color: "white" }}
                onClick={() => props.setTradeScreen("manual_booking")}
              >
                Manual Trade
              </Button>
            </div>
          )}
        </>
      )}

      {hasTrade && (
        <Item>
          <Header
            type="button"
            // onClick={() => setTradeOpen((v) => !v)}
            aria-expanded={tradeOpen}
            aria-controls="submitted-trade-panel"
            $open={tradeOpen}
          >
            <Left>
              <PairBadge>Submitted Trade</PairBadge>
              <Meta title={`Trade ID ${trade.id || ""}`}>
                Ref:{" "}
                <strong style={{ marginLeft: 6 }}>
                  {trade.deal_transaction_id || "—"}
                </strong>
              </Meta>
            </Left>
            <Right>
              {submittedTradeObject && (
                <span
                  style={{
                    marginLeft: 8,
                    padding: "2px 6px",
                    backgroundColor: "#f1f3f5",
                    borderRadius: 4,
                    fontSize: "0.75rem",
                  }}
                >
                  {submittedTradeDeliveryType || "—"}
                </span>
              )}

              <Meta>
                <FiTrendingUp style={{ marginRight: 6 }} /> Trade Rate
              </Meta>
              <Rate>
                {Number.isFinite(Number(trade.deal_rate))
                  ? Number(trade.deal_rate).toFixed(4)
                  : "—"}
              </Rate>
              <Chevron size={18} $open={tradeOpen} aria-hidden />
            </Right>
          </Header>

          <Panel
            id="submitted-trade-panel"
            role="region"
            aria-label="Submitted trade details"
            $open={tradeOpen}
          >
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
              <li
                style={{ padding: "8px 0", borderBottom: "1px solid #f1f3f5" }}
              >
                <Meta>Customer No</Meta>:{" "}
                <strong>{submittedTradeObject?.customer_no || "—"}</strong>
              </li>
              <li
                style={{ padding: "8px 0", borderBottom: "1px solid #f1f3f5" }}
              >
                <Meta>Account Number</Meta>:{" "}
                <strong>{submittedTradeObject?.account_number || "—"}</strong>
              </li>
              <li
                style={{ padding: "8px 0", borderBottom: "1px solid #f1f3f5" }}
              >
                <Meta>Currency Pair</Meta>:{" "}
                <strong>{submittedTradeObject?.currency_pair || "—"}</strong>
              </li>
              <li
                style={{ padding: "8px 0", borderBottom: "1px solid #f1f3f5" }}
              >
                <Meta>Amount</Meta>:{" "}
                <strong>
                  {submittedTradeObject?.amount != null
                    ? Number(submittedTradeObject.amount).toLocaleString(
                        undefined,
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                      )
                    : "—"}
                </strong>
              </li>
              <li
                style={{ padding: "8px 0", borderBottom: "1px solid #f1f3f5" }}
              >
                <Meta>Status</Meta>:{" "}
                <strong
                  style={{
                    padding: "2px 8px",
                    borderRadius: 4,
                    fontSize: "0.85rem",
                    color:
                      tradeStatus === "DEAL_RELEASED" ? "#155724" : "#856404",
                    backgroundColor:
                      tradeStatus === "DEAL_RELEASED" ? "#d4edda" : "#fff3cd",
                    border: `1px solid ${tradeStatus === "DEAL_RELEASED" ? "#c3e6cb" : "#ffeeba"}`,
                  }}
                >
                  {tradeStatus || "PENDING"}
                </strong>
              </li>
              <li
                style={{ padding: "8px 0", borderBottom: "1px solid #f1f3f5" }}
              >
                <Meta>Executed Trade Rate</Meta>:{" "}
                <strong>
                  {Number.isFinite(Number(trade.deal_rate))
                    ? Number(trade.deal_rate).toFixed(4)
                    : "—"}
                </strong>
              </li>
              <li
                style={{ padding: "8px 0", borderBottom: "1px solid #f1f3f5" }}
              >
                <Meta>Trade Date</Meta>:{" "}
                <strong>{submittedTradeObject?.trade_date || "—"}</strong>
              </li>
              <li style={{ padding: "8px 0" }}>
                <Meta>Settlement Date</Meta>:{" "}
                <strong>{submittedTradeObject?.settlement_date || "—"}</strong>
              </li>
            </ul>
            <Button variant="outline-secondary" onClick={() => fetchTradeStatus(trade)}>
              {checkStatusLoading ? "Loading..." : "Check Status"}
            </Button>
          </Panel>
          {/* <Button
            variant="danger"
            style={{ marginTop: 16 }}
            onClick={cancelCapitecTrade}
          >
            {loading ? "Cancelling…" : "Cancel Trade"}
          </Button> */}
        </Item>
      )}

      {!hasTrade && !quote && (
        <div style={{ display: "flex", gap: "12px", marginTop: 16 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              width: "100%",
            }}
          >
            <select
              value={deliveryType}
              onChange={(e) => setDeliveryType(e.target.value)}
              style={{
                width: "250px",
                padding: "10px 14px",
                fontSize: "0.95rem",
                fontFamily: "inherit",
                color: deliveryType ? "#212529" : "#6c757d",
                backgroundColor: "#fff",
                border: "1px solid #ced4da",
                borderRadius: "8px",
                outline: "none",
                cursor: "pointer",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236c757d' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "12px",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#86b7fe";
                e.target.style.boxShadow = "0 0 0 3px rgba(13,110,253,0.15)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#ced4da";
                e.target.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)";
              }}
            >
              <option value="" disabled>
                Select delivery type
              </option>
              {DELIVERY_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <div
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Button
                style={{ width: "250px" }}
                onClick={generateCapitecQuote}
                disabled={!deliveryType || loading}
              >
                {loading ? "Generating Quote..." : "Get Quote"}
              </Button>
              
              {tradeAllowed === true && (
                <Button
                  variant="warning"
                  style={{ width: "250px", minWidth: "250px", maxWidth: "250px" }}
                  onClick={() => props.setTradeScreen("manual_booking")}
                >
                  <span style={{ color: "white" }}>Manual Trade</span>
                </Button>
              )}
            </div>
            {quoteError && (
              <div style={{ color: "red" }}>{quoteErrorMessage}</div>
            )}
          </div>
        </div>
      )}
      {showCancelModal && (
              <ModalOverlay onClick={() => setShowCancelModal(false)}>
                <ModalContent onClick={(e) => e.stopPropagation()}>
                  <ModalTitle>Cancel Trade</ModalTitle>
                  <ModalText>
                    Are you sure you want to cancel this Capitec trade? This action cannot be undone.
                  </ModalText>
                  <ModalActions>
                    <Button variant="outline-secondary" onClick={() => setShowCancelModal(false)}>
                      Go Back
                    </Button>
                    <Button 
                      variant="danger" 
                      onClick={handleCancelTradeConfirm}
                    >
                      Yes, Cancel Trade
                    </Button>
                  </ModalActions>
                </ModalContent>
              </ModalOverlay>
            )}
    </div>
  );
}

export default CapitecTradeComponent;

CapitecTradeComponent.propTypes = {
  inTrade: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    max_exchange_rate: PropTypes.number,
    trade_rate: PropTypes.number,
    trade_amount: PropTypes.number,
    fx_amount: PropTypes.number,
    status: PropTypes.string,
    account_number: PropTypes.number,
    trade_currency: PropTypes.shape({
      alpha_3: PropTypes.string,
    }),
    fx_currency: PropTypes.shape({
      alpha_3: PropTypes.string,
    }),
    quotes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        createdAt: PropTypes.string,
        currencyPair: PropTypes.string,
        amount: PropTypes.number,
        rate: PropTypes.number,
      }),
    ),
  }),
  setTradeScreen: PropTypes.func.isRequired,
};
